import type {Page} from '@PosterWhiteboard/page/page.class';
import type {ItemType} from '@PosterWhiteboard/items/item/item.types';
import type {PageGroupsObject} from '@PosterWhiteboard/page/page-groups.class';
import cloneDeep from 'lodash/cloneDeep';
import {getUniqueString} from '@Utils/string.util';
import {Alignment} from '@PosterWhiteboard/page/alignment.class';
import {getFabricObjectUID} from '@PosterWhiteboard/items/item/item.class';
import {FONT_SIZE_MAX, FONT_SIZE_MIN} from '@PosterWhiteboard/items/text-item/text-item.types';
import type {FabricObject, TPointerEvent} from '@postermywall/fabricjs-2';
import {Point} from '@postermywall/fabricjs-2';
import {Group, ActiveSelection, Textbox, Canvas} from '@postermywall/fabricjs-2';
import type {AudioItem} from '@PosterWhiteboard/classes/audio-clips/audio-item.class';
import {isChildOfGroup} from '@Utils/fabric.util';
import {ITEM_CONTROL_DIMENSIONS} from '@PosterWhiteboard/poster/poster-item-controls';

export enum GroupSpacing {
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical',
}

export enum GroupSpacingAxis {
  X = 'x',
  Y = 'y',
}

const RESIZE_SELECTION_SCALE = {
  DEFAULT: 0.04,
  TEXT: 4,
};

interface DuplicateSelectionOpts {
  sourceGroupHashMap: PageGroupsObject['groupsHashMap'];
  x?: number;
  y?: number;
}

export enum ResizeSelectionKey {
  UP = 'up',
  DOWN = 'down',
}

export class SelectionManager {
  public page: Page;
  public alignment: Alignment;

  public constructor(page: Page) {
    this.page = page;
    this.alignment = new Alignment(this.page);
  }

  public onSelectionCreated(): void {
    const o = this.getActiveObject();
    if (o instanceof ActiveSelection) {
      const selectedViews = this.getSelectedViews() as (FabricObject | Group)[];

      for (let i = 0; i < selectedViews.length; i++) {
        const itemId = getFabricObjectUID(selectedViews[i]);
        const item = this.page.items.getItem(itemId);
        if (selectedViews[i] instanceof Textbox && item && (item.isText() || item.isSlideshow())) {
          selectedViews[i] = item.fabricObject;
          this.discardActiveObject();

          const selectedObjects = new ActiveSelection(selectedViews, {
            canvas: this.page.fabricCanvas,
          });
          this.setActiveObject(selectedObjects);
        }
      }
    }
  }

  public clearSelection(): void {
    const fabricObjects = this.getActiveObjects();
    if (!fabricObjects || fabricObjects.length === 0) {
      return;
    }

    this.discardActiveObject();
    this.page.fabricCanvas.requestRenderAll();
  }

  public removeItemFromSelection(item: FabricObject): void {
    const fabricObjects = this.getActiveObjects();
    if (!fabricObjects || fabricObjects.length === 0) {
      return;
    }

    this.selectFabricObjects(
      fabricObjects.filter((fabricObject) => {
        return !(fabricObject === item || (item instanceof Group && isChildOfGroup(fabricObject, item)));
      })
    );
  }

  public deleteSelectedItems(): void {
    const fabricObjects = this.getActiveObjects();
    if (!fabricObjects || fabricObjects.length === 0) {
      return;
    }

    const uids: string[] = [];

    for (let index = 0; index < fabricObjects.length; index += 1) {
      uids.push(fabricObjects[index].__PMWID);
    }

    this.page.poster.deleteItemByIds(uids);
    this.clearSelection();
  }

  private sortByXaxis(a: FabricObject, b: FabricObject): number {
    const aCor = a.getCornerPoints(a.getCenterPoint());
    const bCor = b.getCornerPoints(b.getCenterPoint());

    const aMin = Math.round(Math.min(aCor.tl.x, aCor.tr.x, aCor.br.x, aCor.bl.x));
    const bMin = Math.round(Math.min(bCor.tl.x, bCor.tr.x, bCor.br.x, bCor.bl.x));

    if (aMin < bMin) return -1;
    if (aMin > bMin) return 1;
    return 0;
  }

  private getFilledSpace(views: FabricObject[], group: Group, axis: GroupSpacingAxis): number {
    let filledSpace = 0;
    const firstItemCorners = views[0].getCornerPoints(views[0].getCenterPoint());
    const lastItemCorners = views[views.length - 1].getCornerPoints(views[views.length - 1].getCenterPoint());
    const firstItemMin = Math.min(firstItemCorners.tl[axis], firstItemCorners.tr[axis], firstItemCorners.br[axis], firstItemCorners.bl[axis]);
    const lastItemMax = Math.max(lastItemCorners.tl[axis], lastItemCorners.tr[axis], lastItemCorners.br[axis], lastItemCorners.bl[axis]);
    let padding: number;
    let min: number;
    let max: number;
    const groupPadding = ITEM_CONTROL_DIMENSIONS.PMW_ITEM_LEGACY_PADDING;

    if (axis === GroupSpacingAxis.X) {
      padding = firstItemMin + group.width / 2 + (group.width / 2 - lastItemMax);
    } else {
      padding = firstItemMin + group.height / 2 + (group.height / 2 - lastItemMax);
    }

    if (padding > groupPadding * 3) {
      padding = groupPadding * 2;
      if (axis === GroupSpacingAxis.X) {
        views[0].set({
          left: padding / 2 + (views[0].left - firstItemMin) - group.width / 2,
        });
      } else if (GroupSpacingAxis.Y) {
        views[0].set({
          top: padding / 2 + (views[0].top - firstItemMin) - group.height / 2,
        });
      }
    }

    for (let index = 0; index < views.length; index += 1) {
      const corners = views[index].getCornerPoints(views[index].getCenterPoint());
      min = Math.min(corners.tl[axis], corners.tr[axis], corners.br[axis], corners.bl[axis]);
      max = Math.max(corners.tl[axis], corners.tr[axis], corners.br[axis], corners.bl[axis]);
      filledSpace += max - min;
    }
    return filledSpace + padding;
  }

  private sortByYaxis(a: FabricObject, b: FabricObject): number {
    const aCor = a.getCornerPoints(a.getCenterPoint());
    const bCor = b.getCornerPoints(b.getCenterPoint());

    const aMin = Math.round(Math.min(aCor.tl.y, aCor.tr.y, aCor.br.y, aCor.bl.y));
    const bMin = Math.round(Math.min(bCor.tl.y, bCor.tr.y, bCor.br.y, bCor.bl.y));

    if (aMin < bMin) return -1;
    if (aMin > bMin) return 1;
    return 0;
  }

  private async onVerticalGroupSpacing(): Promise<void> {
    const group = this.getActiveObject();
    const views = this.getActiveObjects();

    if (!views || !(group instanceof Group)) {
      return;
    }

    if (group.lockMovementX) {
      return;
    }

    views.sort((a: FabricObject, b: FabricObject): number => {
      return this.sortByXaxis(a, b);
    });

    const filledSpace = this.getFilledSpace(views, group, GroupSpacingAxis.X);
    const emptySpace = group.width - filledSpace;
    const spaceBetweenEachItem = emptySpace / (views.length - 1);

    for (let index = 1; index < views.length; index += 1) {
      views[index].set({
        left: views[index - 1].left,
      });

      const prevItemCorners = views[index - 1].getCornerPoints(views[index - 1].getCenterPoint());
      const nextItemCorners = views[index].getCornerPoints(views[index].getCenterPoint());
      const prevMax = Math.max(prevItemCorners.tl.x, prevItemCorners.tr.x, prevItemCorners.br.x, prevItemCorners.bl.x);
      const nextMin = Math.min(nextItemCorners.tl.x, nextItemCorners.tr.x, nextItemCorners.br.x, nextItemCorners.bl.x);
      views[index].set({
        left: views[index].left + (prevMax - views[index - 1].left) + spaceBetweenEachItem + (views[index].left - nextMin),
      });
    }
    this.page.poster.history.addPosterHistory();
    this.page.poster.redux.updateReduxData();
  }

  private async onHorizontalSpacing(): Promise<void> {
    const group = this.getActiveObject();
    const views = this.getActiveObjects();

    if (!views || !(group instanceof Group)) {
      return;
    }

    if (group.lockMovementX) {
      return;
    }

    views.sort((a: FabricObject, b: FabricObject): number => {
      return this.sortByYaxis(a, b);
    });

    const filledSpace = this.getFilledSpace(views, group, GroupSpacingAxis.Y);
    const emptySpace = group.height - filledSpace;
    const spaceBetweenEachItem = emptySpace / (views.length - 1);

    for (let index = 1; index < views.length; index += 1) {
      views[index].set({
        top: views[index - 1].top,
      });

      const prevItemCorners = views[index - 1].getCornerPoints(views[index - 1].getCenterPoint());
      const nextItemCorners = views[index].getCornerPoints(views[index].getCenterPoint());
      const prevMax = Math.max(prevItemCorners.tl.y, prevItemCorners.tr.y, prevItemCorners.br.y, prevItemCorners.bl.y);
      const nextMin = Math.min(nextItemCorners.tl.y, nextItemCorners.tr.y, nextItemCorners.br.y, nextItemCorners.bl.y);
      views[index].set({
        top: views[index].top + (prevMax - views[index - 1].top) + spaceBetweenEachItem + (views[index].top - nextMin),
      });
    }

    this.page.poster.history.addPosterHistory();
    this.page.poster.redux.updateReduxData();
  }

  public onGroupSpacing(type: GroupSpacing): void {
    switch (type) {
      case GroupSpacing.HORIZONTAL:
        this.onHorizontalSpacing().catch((e) => {
          console.error(e);
        });
        break;
      case GroupSpacing.VERTICAL:
        this.onVerticalGroupSpacing().catch((e) => {
          console.error(e);
        });
        break;
      default:
        throw new Error('unhandled spacing type for grouped items');
    }
  }

  public onDuplicate(): void {
    const selectedItems = this.page.getSelectedItems();
    // A background duplicates sequentially — the clone slots in after the original —
    // so route it to the background path; everything else clones in place.
    if (selectedItems.length === 1 && selectedItems[0].isBackground) {
      void this.page.duplicateBackgroundAndSelect(selectedItems[0].uid);
      return;
    }
    if (selectedItems.length !== 0) {
      this.duplicateSelection(selectedItems, {sourceGroupHashMap: this.page.items.groups.groupsHashMap});
      return;
    }

    const selectedAudioItem = this.page.audioClips.getSelectedAudioItem();

    if (!selectedAudioItem) {
      return;
    }

    this.duplicateAudioItem(selectedAudioItem);
  }

  public duplicateAudioItem(audioItem: AudioItem): void {
    void this.page.items.addItems.addAudioItemFromAnotherAudioItem(audioItem);
  }

  public orderGraphicItems(graphicItems: ItemType[]): ItemType[] {
    const views = this.page.fabricCanvas.getObjects();
    const rearrangeItems = [];
    const graphicItemsNotOnFabric = [];
    const orderedGraphicItems = [];
    const graphicItemZIndexHashMap: Record<string, number> = {};

    for (let index = 0; index < views.length; index += 1) {
      const uid = views[index].__PMWID;
      graphicItemZIndexHashMap[uid] = index;
    }

    for (let index = 0; index < graphicItems.length; index += 1) {
      if (graphicItemZIndexHashMap[graphicItems[index].uid] !== undefined) {
        rearrangeItems[graphicItemZIndexHashMap[graphicItems[index].uid]] = graphicItems[index];
      } else {
        graphicItemsNotOnFabric.push(graphicItems[index]);
      }
    }

    for (let index = 0; index < rearrangeItems.length; index += 1) {
      if (rearrangeItems[index]) {
        orderedGraphicItems.push(rearrangeItems[index]);
      }
    }

    for (let index = 0; index < graphicItemsNotOnFabric.length; index += 1) {
      orderedGraphicItems.push(graphicItemsNotOnFabric[index]);
    }

    return orderedGraphicItems;
  }

  public duplicateSelection(graphicItems: ItemType[], {sourceGroupHashMap, x = 15, y = 15}: DuplicateSelectionOpts): void {
    if (graphicItems.length === 0) {
      return;
    }

    const items: ItemType[] = [];
    const itemsToDuplicate = this.orderGraphicItems(graphicItems);
    const groupIdMap = new Map<string, string>();

    for (let index = 0; index < itemsToDuplicate.length; index += 1) {
      const absoluteX = itemsToDuplicate[index].getX();
      const absoluteY = itemsToDuplicate[index].getY();
      const absoluteAngle = itemsToDuplicate[index].getAngle();
      const absoluteScaleX = itemsToDuplicate[index].getScaleX();
      const absoluteScaleY = itemsToDuplicate[index].getScaleY();

      const duplicateItem = cloneDeep(itemsToDuplicate[index]);
      duplicateItem.uid = getUniqueString();

      if (duplicateItem.groupId && itemsToDuplicate.length > 1) {
        let newGroupId = groupIdMap.get(duplicateItem.groupId);
        if (!newGroupId) {
          newGroupId = getUniqueString();
          groupIdMap.set(duplicateItem.groupId, newGroupId);
        }
        duplicateItem.groupId = newGroupId;
      } else {
        duplicateItem.groupId = null;
      }

      duplicateItem.fabricObject.group = undefined;
      duplicateItem.fabricObject.set({
        scaleX: absoluteScaleX,
        scaleY: absoluteScaleY,
        angle: absoluteAngle,
      });

      if (duplicateItem.isSlideshow()) {
        duplicateItem.selectedSlideUID = '';

        for (const [slideId, slide] of Object.entries(duplicateItem.slides.slidesHashMap)) {
          slide.uid = getUniqueString();
          duplicateItem.slides.slidesHashMap[slide.uid] = slide;
          delete duplicateItem.slides.slidesHashMap[slideId];

          const slideIndex = duplicateItem.slides.slidesOrder.indexOf(slideId);
          if (slideIndex !== -1) {
            duplicateItem.slides.slidesOrder[slideIndex] = slide.uid;
          }
        }
      }

      if (duplicateItem.isTranscript()) {
        for (const [subtitleId, subtitle] of Object.entries(duplicateItem.subtitlesHashmap)) {
          subtitle.subtitleUID = getUniqueString();
          duplicateItem.subtitlesHashmap[subtitle.subtitleUID] = subtitle;
          delete duplicateItem.subtitlesHashmap[subtitleId];
        }
      }

      if (x || y) {
        duplicateItem.fabricObject.setXY(new Point(absoluteX + x, absoluteY + y));
      }

      if (duplicateItem.isMenu()) {
        duplicateItem.updateCopiedItems(duplicateItem.itemIds);
      }

      duplicateItem.clearInProgressBackgroundRemoval();

      items.push(duplicateItem);
    }

    for (const [oldGroupId, newGroupId] of groupIdMap) {
      const oldRecord = sourceGroupHashMap[oldGroupId];
      if (oldRecord) {
        this.page.items.groups.groupsHashMap[newGroupId] = {...oldRecord};
      }
    }

    void this.page.items.addItems.addItemsFromItemType(items);
  }

  public selectFabricObjects(fabricObjects: FabricObject[], e?: TPointerEvent): void {
    this.discardActiveObject(e);
    if (fabricObjects.length === 1) {
      this.setActiveObject(fabricObjects[0], e);
    } else if (fabricObjects.length > 1) {
      for (const fabricObject of fabricObjects) {
        this.page.items.getItem(fabricObject.__PMWID)?.stopItemAnimation();
      }
      const groupId = this.page.items.groups.getGroupIdForFabricObjects(fabricObjects);
      if (groupId) {
        this.setActiveObject(this.page.items.groups.createActiveSelectionForGroup(fabricObjects, groupId), e);
      } else {
        this.setActiveObject(new ActiveSelection(fabricObjects, {canvas: this.page.fabricCanvas}), e);
      }
    }
    this.page.fabricCanvas.requestRenderAll();
  }

  public async resizeActiveSelection(e: Event, key = ResizeSelectionKey.UP): Promise<void> {
    const direction = key === ResizeSelectionKey.DOWN ? -1 : 1;

    const activeItems = this.page.getSelectedItems();
    if (!activeItems?.length) {
      return;
    }

    const scale = RESIZE_SELECTION_SCALE.DEFAULT * direction;
    const textFontSize = RESIZE_SELECTION_SCALE.TEXT * direction;

    if (activeItems.length === 1) {
      e.preventDefault();

      if (activeItems[0].fabricObject.lockMovementX) {
        return;
      }

      if (
        activeItems[0].isText() &&
        ((direction === -1 && activeItems[0].textStyles.fontSize > FONT_SIZE_MIN) || (direction === 1 && activeItems[0].textStyles.fontSize < FONT_SIZE_MAX))
      ) {
        let newFontSize = activeItems[0].textStyles.fontSize + textFontSize;
        if (newFontSize < FONT_SIZE_MIN) {
          newFontSize = FONT_SIZE_MIN;
        } else if (newFontSize > FONT_SIZE_MAX) {
          newFontSize = FONT_SIZE_MAX;
        }

        void activeItems[0].updateFromObject({
          textStyles: {
            fontSize: newFontSize,
          },
          width: activeItems[0].fabricObject.width * (newFontSize / activeItems[0].textStyles.fontSize),
        });
      } else if (
        !activeItems[0].isText() &&
        ((activeItems[0].fabricObject.getObjectScaling().x > RESIZE_SELECTION_SCALE.DEFAULT && activeItems[0].fabricObject.getObjectScaling().y > RESIZE_SELECTION_SCALE.DEFAULT) ||
          direction === 1)
      ) {
        void activeItems[0].updateFromObject({
          scaleX: activeItems[0].fabricObject.getObjectScaling().x + scale,
          scaleY: activeItems[0].fabricObject.getObjectScaling().y + scale,
        });
      }
    } else if (activeItems.length > 1) {
      const group = this.getActiveObject();
      if (!group) {
        return;
      }

      if (group.lockMovementX) {
        return;
      }

      if ((group.scaleX > RESIZE_SELECTION_SCALE.DEFAULT && group.scaleY > RESIZE_SELECTION_SCALE.DEFAULT) || direction === 1) {
        await this.updateGroupSelection({
          scaleX: group.scaleX + scale,
          scaleY: group.scaleY + scale,
        });
      }
    }
  }

  public async updateGroupSelection(changes: Record<string, any>, undoable = true): Promise<void> {
    const group = this.getActiveObject();
    if (group instanceof Group) {
      group.set(changes);
      group.setCoords();
      if (undoable) {
        this.page.poster.history.addPosterHistory();
      }
      this.page.poster.redux.updateReduxData();
      this.page.fabricCanvas.requestRenderAll();
    }
  }

  public setActiveObject(object: FabricObject, e?: TPointerEvent): void {
    if (this.page.fabricCanvas instanceof Canvas) {
      this.page.fabricCanvas.setActiveObject(object, e);
      this.page.fabricCanvas.requestRenderAll();
    }
  }

  public getActiveObject(): FabricObject | undefined {
    return this.page.fabricCanvas instanceof Canvas ? this.page.fabricCanvas.getActiveObject() : undefined;
  }

  public discardActiveObject(e?: TPointerEvent): void {
    if (this.page.fabricCanvas instanceof Canvas) {
      this.page.fabricCanvas.discardActiveObject(e);
    }
  }

  public getActiveObjects(): FabricObject[] {
    return this.page.fabricCanvas instanceof Canvas ? this.page.fabricCanvas.getActiveObjects() : [];
  }

  public getSelectedViews(): FabricObject[] {
    const o = this.getActiveObject();
    let views: FabricObject[] = [];
    if (o) {
      if (o instanceof ActiveSelection) {
        views = o.getObjects();
      } else {
        views[0] = o;
      }
    }
    return views;
  }
}
